import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useThemeStore } from './stores/useThemeStore';
import { useAuthStore } from './stores/useAuthStore';
import { ActivityTrackingProvider } from './contexts/ActivityTrackingContext';
import { ROUTES } from './constants/routes';
import ProtectedRoute from '@/constants/ProtectedRoute';
import RoleBasedRoute from '@constants/RoleBasedRoute';
import DashboardRouter from '@/constants/DashboardRouter/DashboardRouter';
import PublicLayout from '@/PageLayout/PublicLayout/MainLayout';
import PrivateLayout from '@/PageLayout/PrivateLayout/Layout/LoggedInLayout';
import './styles/globals.css';

function App() {
  const { theme } = useThemeStore();
  const checkTokenExpiry = useAuthStore(state => state.checkTokenExpiry);

  // Initialize theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Initialize auth - start proactive token refresh timer
  useEffect(() => {
    checkTokenExpiry();
  }, [checkTokenExpiry]);

  return (
    <Router>
      <ActivityTrackingProvider>
        <Routes>
          {/* Public Routes - Auto-generated from ROUTES config */}
          {ROUTES.filter(r => r.isPublic).map(route => {
            const Component = route.component;
            const needsMainLayout = ['/', '/features', '/pricing', '/about', '/contact'].includes(route.path);

            return (
              <Route
                key={route.path}
                path={route.path}
                element={needsMainLayout ? <PublicLayout><Component /></PublicLayout> : <Component />}
              />
            );
          })}

          {/* Protected /workspace route - redirects to role-specific dashboard */}
          <Route path="/workspace" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

          {/* Protected Role-Specific Routes with Layout */}
          <Route element={<ProtectedRoute><PrivateLayout /></ProtectedRoute>}>
            {ROUTES.filter(r => !r.isPublic && !r.path.startsWith('/workspace')).map(route => {
              const Component = route.component;

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    route.allowedRoles && route.allowedRoles.length > 0 ? (
                      <RoleBasedRoute allowedRoles={route.allowedRoles}>
                        <Component />
                      </RoleBasedRoute>
                    ) : (
                      <Component />
                    )
                  }
                />
              );
            })}
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ActivityTrackingProvider>
    </Router>
  );
}

export default App;
